/**
 * Detector de nova versão publicada (deploy).
 *
 * Problema que resolve: depois de um deploy, as abas que já estão abertas
 * continuam rodando o JS antigo até alguém dar Ctrl+F5. Aqui a aba compara o
 * buildId embutido no bundle desta sessão com o /version.json publicado e,
 * quando diferem, libera um botão persistente no rodapé.
 *
 * É um singleton: o estado e os listeners vivem no módulo, então existe apenas
 * UM watch por aba, por mais componentes que chamem useAppUpdate().
 */
import { ref, readonly } from 'vue';

// buildId assado no bundle em tempo de build (ver vue.config.js).
const BUILD_ID = process.env.VUE_APP_CLIENT_BUILD_ID || '';

// Em desenvolvimento não faz probe nenhum.
const EH_DEV = process.env.NODE_ENV !== 'production';

const INTERVALO_PROBE = 60000; // 60s
const DEBOUNCE_FOCO = 5000; // 5s entre probes disparados por foco/visibilidade

const updateAvailable = ref(false);

let iniciado = false;
let timer = null;
let ultimoProbeDeFoco = 0;

/**
 * Consulta o /version.json publicado e compara com o buildId desta sessão.
 * Se o probe falhar (offline, 404, deploy em andamento), não faz nada:
 * o botão só aparece com uma resposta boa e um buildId realmente diferente.
 */
async function probe() {
	// Uma vez detectado, fica true até o reload. Não precisa continuar consultando.
	if (updateAvailable.value) return;

	try {
		const resposta = await fetch(`/version.json?t=${Date.now()}`, {
			cache: 'no-store',
			headers: { pragma: 'no-cache' },
		});

		if (!resposta.ok) return;

		const dados = await resposta.json();

		if (dados && dados.buildId && dados.buildId !== BUILD_ID) {
			updateAvailable.value = true;
			pararWatch();
		}
	} catch (e) {
		// Silencioso de propósito: probe que falha não vira botão.
	}
}

/**
 * Probe disparado quando a aba volta a ficar ativa (foco ou visibilidade).
 * Os dois eventos costumam disparar juntos, por isso o mesmo debounce cobre
 * ambos - senão voltar para a aba geraria duas requisições coladas.
 */
function aoAcordar() {
	if (document.visibilityState !== 'visible') return;

	const agora = Date.now();
	if (agora - ultimoProbeDeFoco < DEBOUNCE_FOCO) return;

	ultimoProbeDeFoco = agora;
	probe();
}

function pararWatch() {
	if (timer) {
		clearInterval(timer);
		timer = null;
	}
	document.removeEventListener('visibilitychange', aoAcordar);
	window.removeEventListener('focus', aoAcordar);
}

function iniciar() {
	if (iniciado) return;
	iniciado = true;

	// Sem buildId (dev ou build sem o plugin) não há o que comparar.
	if (EH_DEV || !BUILD_ID) return;

	probe();
	timer = setInterval(probe, INTERVALO_PROBE);
	document.addEventListener('visibilitychange', aoAcordar);
	window.addEventListener('focus', aoAcordar);
}

/** Recarrega a SPA para pegar o bundle novo. */
function recarregar() {
	window.location.reload();
}

export function useAppUpdate() {
	iniciar();

	return {
		updateAvailable: readonly(updateAvailable),
		recarregar,
		buildId: BUILD_ID,
	};
}
